import React from 'react'

import { FormItem } from '@thg-commerce/gravity-patterns/Form/'
import { Checkbox, CustomDropdown, Input } from '@thg-commerce/gravity-elements'
import { Grid, GridItem } from '@thg-commerce/gravity-system'
import { i18n, useFormattableI18nProperty } from '@thg-commerce/enterprise-core'

import {
  StyledSeparator,
  Label,
  RemoveFormItemButton,
  FieldsContainer,
  ExtraCoverageText,
  StyledButton,
  InfoContainer,
  ResultsTitle,
  SmallText,
  MediumText,
  MetricUnit,
  InputContainer,
  RemoveText,
  RemoveIcon,
  DropDownGridItem,
  AddButton,
  StyledInput,
  DropdownGrid,
  SubmitButtonsGrid,
  LitresOfPaint,
  ShopNowButton,
  GravityPlusIcon,
} from './styles'

export interface CoverageCalculatorFormProps {
  paintCoverage: number
  buyButtonUrl?: string
}

interface DimensionsListProps {
  index: number
  width?: string
  height?: string
}

enum Unit {
  METRES = 'm',
  FEET = 'ft',
  INCHES = 'in',
}

enum FormFieldType {
  WALL = 'wall',
  AREA = 'area',
}

export const CoverageCalculatorForm = (props: CoverageCalculatorFormProps) => {
  const i18nText = {
    metric: i18n('coveragecalculator.metric.label'),
    wallHeight: i18n('coveragecalculator.wallheight.label'),
    wallWidth: i18n('coveragecalculator.wallwidth.label'),
    addAWall: i18n('coveragecalculator.addwall.button.text'),
    excludeAreaHeight: i18n('coveragecalculator.excludeareaheight.label'),
    excludeAreaWidth: i18n('coveragecalculator.excludeareawidth.label'),
    addAnArea: i18n('coveragecalculator.addarea.button.text'),
    numberOfPaintCoats: i18n('coveragecalculator.paintcoats.label'),
    includeCoverageLabel: i18n('coveragecalculator.includecoverage.label'),
    includeCoverageText: i18n('coveragecalculator.includecoverage.text'),
    calculate: i18n('coveragecalculator.calculatebutton.text'),
    reset: i18n('coveragecalculator.resetbutton.text'),
    results: i18n('coveragecalculator.results.title'),
    litresOfPaint: i18n('coveragecalculator.results.paint.volume.text'),
    totalArea: i18n('coveragecalculator.results.basedon.text'),
    coverage: useFormattableI18nProperty(
      'coveragecalculator.results.basedon.coverage.text',
    ),
    shopThePaint: i18n('coveragecalculator.results.basedon.paint.per.text'),
    extraMaterial: i18n('coveragecalculator.results.extramaterial.text'),
    shopNow: i18n('coveragecalculator.results.cta.paint.text'),
    removeWallText: i18n('coveragecalculator.removewall.label'),
    removeAreaText: i18n('coveragecalculator.removearea.label'),
    metresMetric: i18n('coveragecalculator.metric.metres.text'),
    feetMetric: i18n('coveragecalculator.metric.feet.text'),
    inchesMetric: i18n('coveragecalculator.metric.inches.text'),
  }

  const [currentMetric, setCurrentMetric] = React.useState({
    value: i18nText.metresMetric,
    unit: Unit.METRES,
    lastCalculatedMetric: Unit.METRES,
  })
  const [currentNumberOfCoats, setCurrentNumberOfCoats] = React.useState(1)
  const [wallsCount, setWallsCount] = React.useState(1)
  const [wallsList, setWallsList] = React.useState<DimensionsListProps[]>([
    { index: 0 },
  ])
  const [areasCount, setAreasCount] = React.useState(1)
  const [areasList, setAreasList] = React.useState<DimensionsListProps[]>([
    { index: 0 },
  ])
  const [extraCoveragePaint, setExtraCoveragePaint] = React.useState(false)
  const [results, setResults] = React.useState<{
    litresOfPaint?: number
    totalPaintArea?: string
  }>({})
  const [
    extraCoverageMaterialText,
    setExtraCoverageMaterialText,
  ] = React.useState('')

  const metricTypes = [
    {
      key: i18nText.metresMetric,
      value: i18nText.metresMetric,
      displayText: i18nText.metresMetric,
      unit: Unit.METRES,
    },
    {
      key: i18nText.feetMetric,
      value: i18nText.feetMetric,
      displayText: i18nText.feetMetric,
      unit: Unit.FEET,
    },
    {
      key: i18nText.inchesMetric,
      value: i18nText.inchesMetric,
      displayText: i18nText.inchesMetric,
      unit: Unit.INCHES,
    },
  ]

  const numberOfPaintCoats = [
    { key: '1', value: '1', displayText: '1' },
    { key: '2', value: '2', displayText: '2' },
    { key: '3', value: '3', displayText: '3' },
    { key: '4', value: '4', displayText: '4' },
  ]

  const convertToMetres = (unit: string, value: number) => {
    if (unit === Unit.FEET) {
      return value * 0.3048
    }
    return value * 0.0254
  }

  const calculateTotalArea = (list: DimensionsListProps[]) => {
    let area = 0

    list
      .filter((item) => item.width && item.height)
      .map((item) => {
        const wallWidth = Number(item.width)
        const wallHeight = Number(item.height)

        if (currentMetric.unit !== Unit.METRES) {
          area +=
            convertToMetres(currentMetric.unit, wallWidth) *
            convertToMetres(currentMetric.unit, wallHeight)
        } else {
          area += wallWidth * wallHeight
        }
      })

    return area
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setCurrentMetric({
      ...currentMetric,
      lastCalculatedMetric: currentMetric.unit,
    })

    const totalArea = calculateTotalArea(wallsList)
    const totalExcludeArea = calculateTotalArea(areasList)

    const totalPaintArea = totalArea - totalExcludeArea

    let litresOfPaint =
      (totalPaintArea / props.paintCoverage) * currentNumberOfCoats

    if (extraCoveragePaint) {
      litresOfPaint += litresOfPaint * 0.1
      setExtraCoverageMaterialText(i18nText.extraMaterial)
    } else {
      setExtraCoverageMaterialText('')
    }

    setResults({
      totalPaintArea: totalPaintArea.toFixed(1),
      litresOfPaint: Number(litresOfPaint.toFixed(1)),
    })
  }

  const resetForm = () => {
    setResults({})
    setCurrentMetric({
      value: i18nText.metresMetric,
      unit: Unit.METRES,
      lastCalculatedMetric: Unit.METRES,
    })
    setWallsCount(1)
    setWallsList([{ index: 0 }])
    setAreasCount(1)
    setAreasList([{ index: 0 }])
    setCurrentNumberOfCoats(1)
    setExtraCoveragePaint(false)
  }

  const addNewWall = () => {
    wallsList.push({ index: wallsCount })
    setWallsCount(wallsCount + 1)
  }

  const addNewArea = () => {
    areasList.push({ index: areasCount })
    setAreasCount(areasCount + 1)
  }

  const removeWall = (index: number) => {
    setWallsList(wallsList.filter((item) => item.index !== index))
  }

  const removeArea = (index: number) => {
    setAreasList(areasList.filter((item) => item.index !== index))
  }

  const wallOrAreaFormItems = (
    list: DimensionsListProps[],
    removeItem: (index: number) => void,
    widthLabel: string,
    heightLabel: string,
    type: FormFieldType,
  ) => {
    return list.map((item, index) => (
      <FieldsContainer key={item.index}>
        <InputContainer>
          <FormItem offset={0}>
            <StyledInput
              label={widthLabel}
              type="text"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                item.width = event.target.value
              }}
              valueOverride={item.width || ''}
              pattern="^\d+(\.\d{1,2})?$"
              required={type === FormFieldType.WALL}
              data-testid={`${type}-width-input-${index}`}
            />
          </FormItem>
          <MetricUnit>{currentMetric.unit}</MetricUnit>
        </InputContainer>

        <InputContainer>
          <FormItem offset={0}>
            <Input
              label={heightLabel}
              type="text"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                item.height = event.target.value
              }}
              valueOverride={list[index].height || ''}
              pattern="^\d+(\.\d{1,2})?$"
              required={type === FormFieldType.WALL}
              data-testid={`${type}-height-input-${index}`}
            />
          </FormItem>
          <MetricUnit>{currentMetric.unit}</MetricUnit>
        </InputContainer>

        {index !== 0 && (
          <RemoveFormItemButton
            onClick={() => removeItem(item.index)}
            data-testid={`remove-${type}-field-${index}`}
          >
            <RemoveIcon />
            <RemoveText>
              {type === FormFieldType.WALL
                ? i18nText.removeWallText
                : i18nText.removeAreaText}
            </RemoveText>
          </RemoveFormItemButton>
        )}
      </FieldsContainer>
    ))
  }

  return (
    <React.Fragment>
      <form onSubmit={handleSubmit}>
        <Grid>
          <DropDownGridItem colSpan={[12, 6, 6, 6]}>
            <Label>{i18nText.metric}</Label>
            <CustomDropdown
              options={metricTypes}
              aria-label={i18nText.metric}
              onChange={(status) => {
                setCurrentMetric({
                  value: status.key.toString(),
                  unit: metricTypes.filter(
                    (item) => item.key === status.key.toString(),
                  )[0].unit,
                  lastCalculatedMetric: currentMetric.lastCalculatedMetric,
                })
              }}
              width="auto"
              selected={currentMetric.value}
              data-testid="custom-dropdown-metric-form-item"
            />
          </DropDownGridItem>
        </Grid>
        <StyledSeparator />
        {wallOrAreaFormItems(
          wallsList,
          removeWall,
          i18nText.wallWidth,
          i18nText.wallHeight,
          FormFieldType.WALL,
        )}
        <AddButton
          emphasis="low"
          onClick={addNewWall}
          data-testid="add-new-wall-button"
        >
          <GravityPlusIcon /> {i18nText.addAWall}
        </AddButton>
        <StyledSeparator />
        {wallOrAreaFormItems(
          areasList,
          removeArea,
          i18nText.excludeAreaWidth,
          i18nText.excludeAreaHeight,
          FormFieldType.AREA,
        )}
        <AddButton
          emphasis="low"
          onClick={addNewArea}
          data-testid="add-an-area-button"
        >
          <GravityPlusIcon /> {i18nText.addAnArea}
        </AddButton>
        <StyledSeparator />
        <DropdownGrid>
          <DropDownGridItem colSpan={[12, 6, 6, 6]}>
            <Label>{i18nText.numberOfPaintCoats}</Label>
            <CustomDropdown
              options={numberOfPaintCoats}
              aria-label={i18nText.numberOfPaintCoats}
              onChange={(status) => {
                setCurrentNumberOfCoats(Number(status.key.toString()))
              }}
              width="auto"
              selected={currentNumberOfCoats.toString()}
              data-testid="number-of-coats-custom-dropdown"
            />
          </DropDownGridItem>
        </DropdownGrid>
        <InfoContainer>
          <Checkbox
            checked={extraCoveragePaint}
            label={i18nText.includeCoverageLabel}
            getState={(state) => setExtraCoveragePaint(state)}
            transparentBorder
            data-testid="extra-coverage-checkbox"
          />
          <ExtraCoverageText>{i18nText.includeCoverageText}</ExtraCoverageText>
        </InfoContainer>
        <SubmitButtonsGrid rowGap={2}>
          <GridItem rowStart={1} colSpan={6}>
            <StyledButton type="submit" data-testid="submit-calculate-button">
              {i18nText.calculate}
            </StyledButton>
          </GridItem>
          <GridItem rowStart={2} colSpan={6}>
            <StyledButton
              emphasis="medium"
              onClick={resetForm}
              data-testid="reset-form-button"
            >
              {i18nText.reset}
            </StyledButton>
          </GridItem>
        </SubmitButtonsGrid>
      </form>
      {(results.litresOfPaint || results.litresOfPaint === 0) && (
        <InfoContainer data-testid="results-container">
          <ResultsTitle>{i18nText.results}</ResultsTitle>
          <LitresOfPaint data-testid="litres-of-paint-result">
            {results.litresOfPaint}
          </LitresOfPaint>
          <MediumText>{i18nText.litresOfPaint}</MediumText>
          <SmallText>{i18nText.totalArea}</SmallText>
          <MediumText>
            {results.totalPaintArea}
            {currentMetric.lastCalculatedMetric}&sup2;
            {extraCoverageMaterialText}
          </MediumText>
          <SmallText>
            {i18nText.coverage(props.paintCoverage.toString())}
          </SmallText>
          <SmallText>{i18nText.shopThePaint}</SmallText>
          {props.buyButtonUrl && (
            <ShopNowButton
              href={props.buyButtonUrl}
              renderedAs="a"
              data-testid="shop-now-coverage-calculator-button"
            >
              {i18nText.shopNow}
            </ShopNowButton>
          )}
        </InfoContainer>
      )}
    </React.Fragment>
  )
}
